/**
 * Hook and utilities for exporting dashboard elements as branded PNG images.
 *
 * Uses html2canvas for DOM-to-canvas conversion and file-saver for download.
 * Adds "Made with ReefDataHub.com" branding overlay with wave icon to exported images,
 * matching the visual style used in the livestock ShareableImageGrid.
 */
import { useCallback, useState } from 'react';
import { saveAs } from 'file-saver';

/**
 * Build a sanitized filename for export.
 * Format: reef-data-hub-{name}-{date}.png
 */
export function buildExportFilename(chartName: string, extension = 'png'): string {
  const sanitized = chartName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const date = new Date().toISOString().slice(0, 10);
  return `reef-data-hub-${sanitized}-${date}.${extension}`;
}

/** SVG wave path data (from ShareableImageGrid) used for branding icon */
const WAVE_PATH_1 = 'M2 12c1.5-2 3.5-3 5.5-1s3.5 1 5.5-1 3.5-3 5.5-1 3.5 1 5.5-1';
const WAVE_PATH_2 = 'M2 16c1.5-2 3.5-3 5.5-1s3.5 1 5.5-1 3.5-3 5.5-1 3.5 1 5.5-1';

/**
 * Draw the wave icon + "Made with ReefDataHub.com" branding at bottom-right of canvas.
 * Matches the visual style from ShareableImageGrid (wave SVG icon + light gray text).
 * Mutates and returns the canvas.
 *
 * @param canvas - The canvas to draw branding on (expected to be at 2x pixel ratio)
 * @param brandingPadding - Extra white padding added below canvas content for branding (in CSS pixels)
 */
export function addBrandingOverlay(
  canvas: HTMLCanvasElement,
  brandingPadding = 0,
): HTMLCanvasElement {
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  // If branding padding is requested, extend the canvas with white space at the bottom
  if (brandingPadding > 0) {
    const paddingPx = brandingPadding * 2; // 2x pixel ratio
    const newCanvas = document.createElement('canvas');
    newCanvas.width = canvas.width;
    newCanvas.height = canvas.height + paddingPx;
    const newCtx = newCanvas.getContext('2d');
    if (!newCtx) return canvas;

    newCtx.fillStyle = '#ffffff';
    newCtx.fillRect(0, 0, newCanvas.width, newCanvas.height);
    newCtx.drawImage(canvas, 0, 0);

    // Copy over to original canvas
    canvas.width = newCanvas.width;
    canvas.height = newCanvas.height;
    ctx.drawImage(newCanvas, 0, 0);
  }

  // All measurements in 2x pixel space (canvas is rendered at 2x pixelRatio)
  const scale = 28 / 24; // Scale wave icon from 24x24 viewBox to 14x14 CSS px (28px at 2x)
  const iconSize = 28; // 14 CSS px at 2x
  const textFontSize = 22; // 11 CSS px at 2x
  const gap = 10; // 5 CSS px at 2x
  const marginRight = 24; // 12 CSS px at 2x
  const marginBottom = 16; // 8 CSS px at 2x

  // Draw the text first to measure its width
  ctx.font = `${textFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
  const textWidth = ctx.measureText('Made with ReefDataHub.com').width;

  // Position the branding block at bottom-right
  const blockWidth = iconSize + gap + textWidth;
  const blockX = canvas.width - marginRight - blockWidth;
  const blockY = canvas.height - marginBottom - iconSize;

  // Draw wave icon using Path2D
  ctx.save();
  ctx.translate(blockX, blockY);
  ctx.scale(scale, scale);

  // Wave path 1 (full opacity)
  const path1 = new Path2D(WAVE_PATH_1);
  ctx.strokeStyle = '#4dabf7';
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.globalAlpha = 0.5;
  ctx.stroke(path1);

  // Wave path 2 (half opacity)
  const path2 = new Path2D(WAVE_PATH_2);
  ctx.globalAlpha = 0.25;
  ctx.stroke(path2);

  ctx.restore();

  // Draw text
  ctx.globalAlpha = 1;
  ctx.font = `${textFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
  ctx.fillStyle = '#b0bec5';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(
    'Made with ReefDataHub.com',
    blockX + iconSize + gap,
    blockY + iconSize / 2,
  );

  return canvas;
}

interface ExportImageOptions {
  /** Extra white padding in CSS pixels added below content for branding (default: 0) */
  brandingPadding?: number;
}

interface UseExportImageReturn {
  /** Export a DOM element as a branded PNG */
  exportElement: (element: HTMLElement, chartName: string, options?: ExportImageOptions) => Promise<void>;
  /** Whether an export is currently in progress */
  isExporting: boolean;
}

/**
 * Hook for exporting DOM elements as branded PNG images.
 *
 * Usage:
 * ```tsx
 * const { exportElement, isExporting } = useExportImage();
 * <button onClick={() => exportElement(chartRef.current!, 'Temperature')}>Export</button>
 * ```
 */
export function useExportImage(): UseExportImageReturn {
  const [isExporting, setIsExporting] = useState(false);

  const exportElement = useCallback(async (
    element: HTMLElement,
    chartName: string,
    options?: ExportImageOptions,
  ) => {
    setIsExporting(true);
    try {
      // Lazy-load html2canvas for code splitting — uses direct canvas drawing
      // instead of SVG foreignObject, avoiding Safari/iOS blank-image bugs.
      const html2canvas = (await import('html2canvas')).default;

      // Capture the element as canvas (2x scale for quality)
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
      });

      // Add branding overlay
      addBrandingOverlay(canvas, options?.brandingPadding);

      // Convert to blob and download
      await new Promise<void>((resolve, reject) => {
        canvas.toBlob((blob: Blob | null) => {
          if (blob) {
            const filename = buildExportFilename(chartName);
            saveAs(blob, filename);
            resolve();
          } else {
            reject(new Error('Failed to generate image blob'));
          }
        }, 'image/png');
      });
    } catch (error) {
      console.error('Failed to export chart:', error);
      throw error;
    } finally {
      setIsExporting(false);
    }
  }, []);

  return { exportElement, isExporting };
}
